import {BrokerKey} from "./BrokerKey";
import {Branding} from "./Branding";
import {ChartIQSerializedLayout} from "../components/chartiq/types/ChartIQ";
import {BackOffice} from "./BackOffice";
import {StripeUserSubscriptionRecord} from "../stripe/models/StripeUserSubscriptionRecord";

export interface UserConfig {
    loggedIn: boolean;
    orderAdviceAccess: boolean;
    id: number;
    username: string;
    shortName: string;
    alias?: any;
    backOffice: BackOffice;
    broker: BrokerKey;
    branding: Branding;
    professional: boolean;
    uid: string;
    contextHelp: boolean;
    imageUrl: string;
    browser: string;
    stripePublishableKey: string;
    hasCard: boolean;
    subscriptions: Array<StripeUserSubscriptionRecord>;
    stripeCustomerId: string;
    authorizedRepresentative: boolean;
    eodView: boolean;
    client?: Client;
    adviser?: Adviser;
    riskManager?: RiskManager;
    support: AdminUser;
    super: SuperUser;
    strategyBasedView: boolean;
    watchlist: ReadonlyArray<string>;
    livePrices: boolean;
    profile: Profile;
    features: Features;
    favourites: Favourite[];
    preferences: Preferences;
    securitySetting: string;
    isMfaEnabled: boolean;
    hasSwiftApplication: boolean;
    bannerMessage: string;

    ordersEnabled: boolean; // This is set in app.js...
    viewOrdersEnabled: boolean; // As above ...

    refresh(): Promise<void>;
}

export interface Legal {
    terms: Date;
    privacy: Date;
}

export interface Client {
    watchedTmcVideo: boolean;
    customerNumber: string;
    passedOptionsQuiz: boolean;
    legal: Legal;
    onBoarded: Date;
    referrer?: string
}

export type Adviser = {};
export type RiskManager = {};
export type SuperUser = {};
export type AdminUser = {};

export interface Profile {
    givenName: string;
    surname: string | null;
    age: boolean;
    state: string;
    postCode?: string;
    country: boolean;
    phone?: string;
    updated: Date;
    sophisticated: boolean | null;
}

export enum FeatureNames {
    FEATURE_LIVE_PRICES = "FEATURE_LIVE_PRICES",
    FEATURE_DEMO_PRICES = "FEATURE_DEMO_PRICES",
    FEATURE_UNLIMITED_WHAT_IF = "FEATURE_UNLIMITED_WHAT_IF",
    FEATURE_OPEN_POS_TABLE = "FEATURE_OPEN_POS_TABLE",
    FEATURE_PF_STRESS_TEST = "FEATURE_PF_STRESS_TEST",
    FEATURE_OPTION_POSITIONS = "FEATURE_OPTION_POSITIONS",
    FEATURE_SHARE = "FEATURE_SHARE",
    FEATURE_ADV_CHARTING = "FEATURE_ADV_CHARTING",
    FEATURE_ORDER_ENTRY = "FEATURE_ORDER_ENTRY",
    FEATURE_PF_STRESS_TEST_LIMITED = "FEATURE_PF_STRESS_TEST_LIMITED",
    FEATURE_ACCOUNT_PERFORMANCE = "FEATURE_ACCOUNT_PERFORMANCE",

    FEATURE_POST_CREATE = "FEATURE_POST_CREATE",
    FEATURE_POST_ADMIN = "FEATURE_POST_ADMIN",
    FEATURE_COMMENT_CREATE = "FEATURE_COMMENT_CREATE",
    FEATURE_COMMENT_ADMIN = "FEATURE_COMMENT_ADMIN",

    CLEARING_DASHBOARD = "CLEARING_DASHBOARD",
    CLEARING_DASHBOARD_ADMIN = "CLEARING_DASHBOARD_ADMIN",

    EDUCATION = "EDUCATION",

    SCREEN_BACKTESTING = "SCREEN_BACKTESTING",
    SCREEN_IV_SALES_ADMIN = "SCREEN_IV_SALES_ADMIN",
    SCREEN_TRADING_SYSTEMS = "SCREEN_TRADING_SYSTEMS",
    SCREEN_STRATEGY_BUILDER = "SCREEN_STRATEGY_BUILDER",
    SCREEN_EQUITY_ORDERS = "SCREEN_EQUITY_ORDERS",
    SCREEN_OPTION_ORDERS = "SCREEN_OPTION_ORDERS",

    TRADING_ALERTS = "TRADING_ALERTS",
    LITT_TRADING_SYSTEM = "LITT_TRADING_SYSTEM",
    OPTIONS_COOKBOOK = "OPTIONS_COOKBOOK",
    STOCK_SCREENER = "STOCK_SCREENER",

    WIDGET_RA_PORTFOLIO = "WIDGET_RA_PORTFOLIO",
    WIDGET_RA_SECURITY = "WIDGET_RA_SECURITY",
    WIDGET_DELTA_ANALYSIS = "WIDGET_DELTA_ANALYSIS",
    EXTENSION_DELTA_ANALYSIS_ADV = "EXTENSION_DELTA_ANALYSIS_ADV",
    WIDGET_IC = "WIDGET_IC",
    WIDGET_SECURITY_BREAKDOWN = "WIDGET_SECURITY_BREAKDOWN",
    WIDGET_WHAT_IF = "WIDGET_WHAT_IF",
    WIDGET_TC_PAYOFF = "WIDGET_TC_PAYOFF",
    WIDGET_TF_PAYOFF = "WIDGET_TF_PAYOFF",
    WIDGET_WHAT_IF_COMPARISON = "WIDGET_WHAT_IF_COMPARISON",

    SCREEN_ADVISER = "SCREEN_ADVISER",
    SCREEN_CLIENT = "SCREEN_CLIENT",
    SCREEN_RISK = "SCREEN_RISK",
    SCREEN_USER_MAINT = "SCREEN_USER_MAINT",

    SCREEN_PASSWORD_POLICY = "SCREEN_PASSWORD_POLICY",
    SCREEN_ORG_STRUCTURE = "SCREEN_ORG_STRUCTURE",
    SCREEN_USER_LIST = "SCREEN_USER_LIST",
    SCREEN_ADMIN = "SCREEN_ADMIN",
    SCREEN_VOLATILITY_TOOLS = "SCREEN_VOLATILITY_TOOLS",
    SCREEN_OPTIONS_CHAIN = "SCREEN_OPTIONS_CHAIN",
    SCREEN_TRADING_TOOLS = "SCREEN_TRADING_TOOLS",
    SCREEN_SUPPORT_MANAGER = "SCREEN_SUPPORT_MANAGER",

    SCREEN_ACCOUNT_LIST = "SCREEN_ACCOUNT_LIST",
    SCREEN_OPTIONS_POSITIONS_SEARCH = "SCREEN_OPTIONS_POSITIONS_SEARCH",
    SCREEN_EQUITY_POSITIONS_SEARCH = "SCREEN_EQUITY_POSITIONS_SEARCH",
    SCREEN_RISK_MANAGER = "SCREEN_RISK_MANAGER",
    SCREEN_EQUITY_ACCOUNT = "SCREEN_EQUITY_ACCOUNT",
    SCREEN_OPTIONS_ACCOUNT = "SCREEN_OPTIONS_ACCOUNT",

    SCREEN_ADVISER_DASHBOARD = "SCREEN_ADVISER_DASHBOARD",
    SCREEN_COMPANY_INFO = "SCREEN_COMPANY_INFO",
    SIDEBAR_WATCHLIST = "SIDEBAR_WATCHLIST",
    SIDEBAR_NEWS = "SIDEBAR_NEWS",

    NEWS = "NEWS",
    STOP_LOSS_ORDERS = "STOP_LOSS_ORDERS",
    AVERAGE_PRICE_UPDATE = "AVERAGE_PRICE_UPDATE",

    ADMIN_LOGIN = "ADMIN_LOGIN",
    CMA_UPDATE = "CMA_UPDATE"
}

export type Features = {
    [K in FeatureNames]?: boolean
};

export interface Favourite {
    id: number;
    name: string;
    scratch: boolean;
    type: string;
}

export type ThemeType = "light" | "dark";

export interface Preferences {
    "portfolio.widgets"?: Array<WidgetPreference>;
    charting?: ChartingPreferences;
    theme?: ThemeType;
    priceInCents?: boolean;
}

export interface WidgetPreference {
    name: string;
    row: number;
    col: number;
    sizeX: number;
    sizeY: number;
}

export interface UserConfigProvider {
    config: UserConfig;
}

export interface ChartingPreferences {
    [key: number]: ChartConfiguration;
}

export interface ChartConfiguration {
    layout: ChartIQSerializedLayout;
    drawings: { [key: string]: any[] };
    hud: boolean;
    symbol: string;
}
